/**
 * Cal.com configuration for Coupling WNC booking flow.
 *
 * Single source of truth for the Cal.com handle and event slugs.
 * Update CAL_HANDLE if the cal.com username changes; everything else
 * (links, embeds, deep-link route) reads from these constants.
 *
 * Public booking URLs:
 *   https://cal.com/{CAL_HANDLE}/{INTRO_SLUG}   ← short qualifying call
 *   https://cal.com/{CAL_HANDLE}/{DEMO_SLUG}    ← full product walkthrough
 */

import { demoTopics } from '../content/siteMessaging';

export const CAL_HANDLE = 'coupling';

// Maps internal event-type keys → actual Cal.com URL slugs.
//   cal.com/coupling/intro → 15-min intro chat
//   cal.com/coupling/demo  → 30-min product demo
export const CAL_EVENT_SLUGS = {
  intro: 'intro',
  demo: 'demo',
} as const;

export type CalEventType = keyof typeof CAL_EVENT_SLUGS;

/** cal.com link string used by @calcom/embed-react: "<handle>/<slug>". */
export const calLink = (eventType: CalEventType): string =>
  `${CAL_HANDLE}/${CAL_EVENT_SLUGS[eventType]}`;

/** Full https URL — useful for `target="_blank"` fallbacks and email signatures. */
export const calUrl = (eventType: CalEventType): string =>
  `https://cal.com/${calLink(eventType)}`;

/** Brand color passed to Cal.com embed UI to match the WNC site theme. */
export const CAL_BRAND_COLOR = '#E36C3E';

/** Human-readable labels for each event type. Used in CTAs, modal copy, and /book page SEO. */
export const CAL_EVENT_LABELS: Record<
  CalEventType,
  { short: string; long: string; description: string; seoTitle: string; seoDescription: string }
> = {
  intro: {
    short: 'Book a 15-min call',
    long: '15-min intro chat',
    description:
      'Quick call to learn about your business, answer questions, and see whether Coupling WNC fits your shop.',
    seoTitle: 'Book a Coupling WNC intro call',
    seoDescription:
      'Book a quick Coupling WNC intro call to ask questions and see whether checkout, appointments, and rewards fit your shop.',
  },
  demo: {
    short: 'Book a demo',
    long: '30-min product demo',
    description:
      `See how ${demoTopics} would work for your shop.`,
    seoTitle: 'Book a Coupling WNC product demo',
    seoDescription:
      `Book a Coupling WNC product demo and choose a time to see how ${demoTopics} would work for your shop.`,
  },
};
